import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { portalApi } from '@/lib/api/portal.js';
import { usePortalAuthStore } from '@/stores/portal-auth.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import { PortalProgress } from '@/components/portal/PortalProgress.js';

export function PortalDashboardPage() {
  const customer = usePortalAuthStore((s) => s.customer);
  const meQuery = useQuery({ queryKey: ['portal', 'me'], queryFn: portalApi.me });
  const ticketsQuery = useQuery({
    queryKey: ['portal', 'tickets', 'active'],
    queryFn: () => portalApi.tickets({ status: 'active', pageSize: 5 }),
  });
  const estimatesQuery = useQuery({
    queryKey: ['portal', 'estimates'],
    queryFn: portalApi.estimates,
  });

  const me = meQuery.data;
  const activeTickets = ticketsQuery.data?.data ?? [];
  const estimates = (estimatesQuery.data ?? []) as Array<{
    id: string;
    estimateNumber: string;
    deviceSummary: string;
    total: string;
    validUntil: string | null;
    approvalToken: string | null;
  }>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-950">
          Welcome back, {customer?.firstName ?? me?.firstName ?? 'there'}!
        </h1>
        <p className="mt-1 text-sm text-slate-500">Track repairs, estimates, and rewards in one place.</p>
      </div>

      {me?.canCreateTickets ? (
        <Link
          to="/portal/tickets/new"
          className="inline-flex rounded-xl bg-[#E8793A] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#d66a2e]"
        >
          + New Repair Request
        </Link>
      ) : null}

      <section>
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">Active Repairs</h2>
        <div className="space-y-3">
          {activeTickets.map((t) => (
            <div
              key={t.id}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold text-slate-950">{t.ticketNumber}</div>
                  <div className="text-sm text-slate-600">{t.deviceSummary}</div>
                  <div className="text-sm text-slate-500">{t.repairSummary}</div>
                </div>
                <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800 capitalize">
                  {t.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="mt-3">
                <PortalProgress index={t.progressIndex} />
              </div>
              {t.promisedDate ? (
                <p className="mt-2 text-sm text-slate-500">
                  Est. ready: {formatDateTime(t.promisedDate)}
                </p>
              ) : null}
              <Link
                to={`/portal/tickets/${t.id}`}
                className="mt-3 inline-block text-sm font-semibold text-[#1B4F8A]"
              >
                View Details →
              </Link>
            </div>
          ))}
          {activeTickets.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              No active repairs right now.
            </p>
          ) : null}
        </div>
      </section>

      {estimates.length > 0 ? (
        <section>
          <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">
            Pending Estimates
          </h2>
          <div className="space-y-3">
            {estimates.map((e) => (
              <div key={e.id} className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
                <div className="flex justify-between gap-3">
                  <div>
                    <div className="font-bold">{e.estimateNumber}</div>
                    <div className="text-sm text-slate-600">{e.deviceSummary}</div>
                    <div className="mt-1 font-semibold">Total: {formatCurrency(e.total)}</div>
                  </div>
                  <span className="text-xs font-semibold text-amber-700">Awaiting Your Approval</span>
                </div>
                {e.approvalToken ? (
                  <Link
                    to={`/estimates/approve/${e.approvalToken}`}
                    className="mt-3 inline-block text-sm font-semibold text-[#1B4F8A]"
                  >
                    Review & Approve →
                  </Link>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Loyalty Points" value={String(me?.loyaltyPointsBalance ?? 0)} />
        <Stat label="Store Credit" value={formatCurrency(me?.storeCreditBalance ?? 0)} />
        <Stat label="Total Repairs" value={String(me?.totalTickets ?? 0)} />
        <Stat label="Active Repairs" value={String(activeTickets.length)} />
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-bold text-slate-950">{value}</div>
    </div>
  );
}
